import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Input, Form, FormGroup, Label, Col, Row } from 'reactstrap';
import { useHistory, useParams } from 'react-router-dom';
import { connect, useSelector } from 'react-redux';

import IUserGet from 'api/interfaces/users/IUserGet';
import SelectInputFieldControlled from 'components/layout/forms/SelectInputFieldControlled';
import { EMAIL_FORMATS, makeEmail } from 'services/contacts/emailFormats';

import ApiErrorTypes from 'services/enums/ApiErrorTypes';
import ModalAccept from 'components/layout/modals/ModalAccept';
import ModalConfirm from 'components/layout/modals/ModalConfirm';
import PageContainer from 'components/layout/PageContainer';
import PrimaryButton from 'components/layout/buttons/PrimaryButton';
import {
  CHECK_CREDIT,
  DELETE_CONTACTS,
  WRITE_CONTACTS,
} from 'services/enums/users/permissions';
import {
  CREDIT_RATING,
  toOptions,
  useChoiceList,
} from 'services/offers/offerChoices';
import useHasPermission from 'services/users/useHasPermission';
import MultiSelectDropdown from 'components/layout/forms/MultiSelectDropdown';
import TextInputField from 'components/layout/forms/TextInputField';
import DateInput from 'components/layout/forms/DateInput';
import IOrganizationUnit from 'interfaces/users/IOrganizationUnit';
import { fetchOrganizationUnits } from 'store/users/actions';
import { addMessage as addMessageAction } from 'store/messages/actions';
import customerApi from '../../api/contacts';
import translations from '../../services/translations/translations';
import showOrganizationUnits from './showOrganizationUnits';
import useContactTypeOptions from '../../services/contacts/useContactTypeOptions';
import sortResult from '../../services/sort/sortResult';
import '../../style/contacts/Contacts.css';
import '../../style/contacts/CreateContact.css';
import filterAndSortPersons from './filterAndSortPersons';
import CreditCheckBadge from './CreditCheckBadge';
import ContactBranches from './ContactBranches';
import LinkedOffers from './LinkedOffers';
import CompanyPersons from './CompanyPersons';

interface customerType {
  // Apunimi: the auxiliary business name, empty when none
  auxiliaryName: string;
  city: string;
  country: string;
  postalNumber: string;
  streetAddress: string;
  businessId: string;
  companyName: string;
  contactTypes: string[];
  // The day the credit information was last checked (yyyy-mm-dd), empty when never
  creditCheckedAt: string;
  // Luottoraja as typed, empty when not given
  creditLimit: string;
  // Luottoluokka of the choice list, empty when not given
  creditRating: string;
  emailEnding: string;
  // EMAIL_FORMATS, e.g. firstname.lastname; empty when not given
  emailFormat: string;
  organizationUnitIds: number[];
}

const CreateContact = ({
  addMessage,
  organizationUnitTypesList,
  fetchOrganizationUnitTypesList,
}: {
  addMessage: (message: string) => void;
  organizationUnitTypesList: IOrganizationUnit[];
  fetchOrganizationUnitTypesList: () => void;
}): JSX.Element | null => {
  const { t } = useTranslation([
    translations.contacts,
    translations.common,
    translations.admin,
    translations.products,
  ]);
  // The names of the e-mail forms, read as one object: the forms have dots (e.g.
  // firstname.lastname), which a key of their own would take as nested keys
  const emailFormatNames = t('emailFormats', {
    returnObjects: true,
  }) as unknown as Record<string, string>;
  const history = useHistory();
  const canWrite = useHasPermission(WRITE_CONTACTS);
  // The admins remove a company not in use, and lock one in use
  const canDelete = useHasPermission(DELETE_CONTACTS);
  // Locked: not chosen for new offers, projects or products; in use: not removed
  const [locked, setLocked] = useState(false);
  const [inUse, setInUse] = useState(false);
  const { contactId } = useParams();
  const [persons, setPersons] = useState<any[]>([]);
  const [contactTypes, setContactTypes] = useState<string[]>([]);
  const [deleteSuccess, setDeleteSuccess] = useState(false);
  const [modalDeleteConfirmation, setModalDeleteConfirmation] = useState(false);
  const [modalDelete, setModalDelete] = useState(false);
  const toggleDelete = () => setModalDelete(!modalDelete);
  const toggleDeleteConfirmation = () =>
    setModalDeleteConfirmation(!modalDeleteConfirmation);

  const [customer, setCustomer] = useState<customerType>({
    auxiliaryName: '',
    city: '',
    country: '',
    postalNumber: '',
    streetAddress: '',
    businessId: '',
    companyName: '',
    contactTypes: [],
    creditCheckedAt: '',
    creditLimit: '',
    creditRating: '',
    emailEnding: '',
    emailFormat: '',
    organizationUnitIds: [],
  });
  // The credit check as saved, with who marked it
  const [savedCredit, setSavedCredit] = useState<{
    checkedAt: string;
    checkedUserName: null | string;
  }>({ checkedAt: '', checkedUserName: null });
  const canCheckCredit = useHasPermission(CHECK_CREDIT);
  const creditRatings = useChoiceList(CREDIT_RATING, customer.creditRating);

  // A new contact of a user of a Finnish department is in Finland unless changed
  const currentUser = useSelector(
    (state: { users: { current: IUserGet | null } }) => state.users.current
  );
  const userCountry = currentUser?.organizationUnit?.country ?? 'FI';
  useEffect(() => {
    if (contactId === undefined && currentUser && userCountry === 'FI') {
      setCustomer((prevState) =>
        prevState.country === ''
          ? { ...prevState, country: t('Finland') }
          : prevState
      );
    }
  }, [contactId, currentUser, userCountry]);
  // The types as the admins have listed them; a hidden type the contact has is kept
  const typeOptions = useContactTypeOptions(
    contactTypes,
    customer.contactTypes
  ).options;

  const [organizationUnits, setOrganizationUnits] = useState<
    { label: string; value: number }[]
  >([]);

  const handleChange = (e: any) => {
    const { name, value } = e.target;
    setCustomer((prevState) => ({
      ...prevState,
      [name]: value,
    }));
  };

  const handleMultiSelectChange = (e: any) => {
    const { checked: value, name: selected } = e.target;
    const { contactTypes: cTypes } = customer;

    if (value === true) {
      cTypes.push(selected);
    } else {
      const typeIndex = cTypes.findIndex((curr) => curr === selected);
      if (typeIndex >= 0) {
        cTypes.splice(typeIndex, 1);
      }
    }

    setCustomer((prevState) => ({
      ...prevState,
      contactTypes: cTypes,
    }));
  };

  const handleOrganizationUnits = (e: any) => {
    setOrganizationUnits(e);
  };

  const goPreviousPage = () => {
    history.push('/contacts');
  };

  useEffect(() => {
    fetchOrganizationUnitTypesList();
    const getContact = async () => {
      if (contactId !== undefined) {
        try {
          const result: any = await customerApi.getContact(contactId);
          if (result !== undefined && result.removed) {
            goPreviousPage();
          } else if (result !== undefined) {
            setOrganizationUnits(
              showOrganizationUnits(result?.organizationUnits)
            );
            setCustomer({
              auxiliaryName: result.auxiliaryName ?? '',
              city: result.address.city,
              country: result.address.country,
              postalNumber: result.address.postalNumber,
              streetAddress: result.address.streetAddress,
              businessId: result.businessId,
              companyName: result.companyName,
              contactTypes: result.contactTypes,
              creditCheckedAt: (result.creditCheckedAt ?? '').slice(0, 10),
              creditLimit:
                result.creditLimit === null || result.creditLimit === undefined
                  ? ''
                  : String(result.creditLimit),
              creditRating: result.creditRating ?? '',
              emailEnding: result.emailEnding,
              emailFormat: result.emailFormat ?? '',
              organizationUnitIds: [],
            });

            setSavedCredit({
              checkedAt: (result.creditCheckedAt ?? '').slice(0, 10),
              checkedUserName: result.creditCheckedUserName ?? null,
            });
            setPersons(filterAndSortPersons(result.contactPersons));
            setLocked(result.locked === true);
            setInUse(result.inUse === true);
          }
        } catch (error) {
          // define action
        }
      }
    };
    getContact().catch((e) => console.log(e));
  }, [contactId]);

  const addUpdateContact = async () => {
    const formattedContact = {
      address: {
        city: customer.city,
        country: customer.country,
        postalNumber: customer.postalNumber,
        streetAddress: customer.streetAddress,
      },
      auxiliaryName: customer.auxiliaryName.trim() || null,
      businessId: customer.businessId,
      companyName: customer.companyName,
      contactTypes: customer.contactTypes,
      creditCheckedAt: customer.creditCheckedAt || null,
      // E.g. "50 000,50" -> 50000.5
      creditLimit:
        customer.creditLimit.trim() === ''
          ? null
          : Number(customer.creditLimit.replace(/\s/g, '').replace(',', '.')),
      creditRating: customer.creditRating || null,
      emailEnding: customer.emailEnding,
      emailFormat: customer.emailFormat || null,
      organizationUnitIds: organizationUnits.map((org) => org.value),
    };
    let result;
    try {
      if (contactId === undefined) {
        result = await customerApi.post(formattedContact);
        addMessage(t(`${translations.contacts}:Contact saved`));
        history.push(`/contacts/${result.id}`);
      } else {
        result = await customerApi.putContact(contactId, formattedContact);
        addMessage(t(`${translations.contacts}:Contact saved`));
        // The checker of the credit check is marked by the server
        const saved: any = await customerApi.getContact(contactId);
        setSavedCredit({
          checkedAt: (saved?.creditCheckedAt ?? '').slice(0, 10),
          checkedUserName: saved?.creditCheckedUserName ?? null,
        });
      }
    } catch (error) {
      const organizationUnitMismatch =
        error.response.status === 400 &&
        error.response.data.message === ApiErrorTypes.OrganizationUnitMismatch;

      if (organizationUnitMismatch) {
        addMessage(t(`${translations.contacts}:Organization unit mismatch`));
      } else {
        addMessage(t(`${translations.contacts}:Contact failed`));
      }
    }
  };

  const deleteContact = async () => {
    try {
      const result = await customerApi.deleteContact(contactId);
      if (result !== undefined && result.success === true) {
        toggleDelete();
        toggleDeleteConfirmation();
        setDeleteSuccess(true);
      } else {
        setDeleteSuccess(false);
      }
    } catch (error) {
      setDeleteSuccess(false);
      toggleDelete();
      // A company in use is only locked
      const { response } = error as {
        response?: { data?: { message?: string } };
      };
      addMessage(
        t(
          response?.data?.message === 'Contact in use'
            ? `${translations.contacts}:lock.In use`
            : `${translations.contacts}:Delete Failure`
        )
      );
    }
  };

  const toggleLocked = async () => {
    if (contactId === undefined) {
      return;
    }

    try {
      await customerApi.patchLocked(contactId, !locked);
      setLocked(!locked);
      addMessage(
        t(`${translations.contacts}:lock.${locked ? 'Unlocked' : 'Locked'}`)
      );
    } catch (error) {
      addMessage(t(`${translations.contacts}:lock.Failed`));
    }
  };

  const addPerson = () => {
    history.push(`/contacts/${contactId}/create`);
  };

  const typeIncluded = (cType: string) => {
    return customer.contactTypes.findIndex((name) => name === cType) !== -1;
  };

  useEffect(() => {
    const getContactTypes = async () => {
      try {
        const result: string[] = await customerApi.getTypes();
        if (result !== undefined) {
          setContactTypes(sortResult(result, t));
        }
      } catch (error) {
        // define action
      }
    };
    getContactTypes().catch((e) => console.log(e));
  }, []);

  return (
    <PageContainer>
      <h2 className="header-text">{t('Contact Info')}</h2>
      <div className="content-background">
        {/* A locked company is seen by all, so nobody wonders why it cannot be chosen */}
        {locked ? (
          <p className="contact-locked-info" role="status">
            <span aria-hidden>🔒</span>{' '}
            {t(`${translations.contacts}:lock.Info`)}
          </p>
        ) : null}
        <Form>
          <Row>
            <Col>
              {/* The auxiliary business name next to the name of the company */}
              <Row>
                <Col>
                  <TextInputField
                    label={t('Company Name')}
                    name="companyName"
                    onChange={handleChange}
                    value={customer.companyName}
                  />
                </Col>
                <Col>
                  <TextInputField
                    label={t('Auxiliary name')}
                    name="auxiliaryName"
                    onChange={handleChange}
                    value={customer.auxiliaryName}
                  />
                </Col>
              </Row>
              <TextInputField
                label={t('Street Address')}
                name="streetAddress"
                onChange={handleChange}
                value={customer.streetAddress}
              />
              <TextInputField
                label={t('City')}
                name="city"
                onChange={handleChange}
                value={customer.city}
              />
              <TextInputField
                label={t('Country')}
                name="country"
                onChange={handleChange}
                value={customer.country}
              />
              <TextInputField
                label={t('Email Ending')}
                name="emailEnding"
                onChange={handleChange}
                value={customer.emailEnding}
              />
              {/* The e-mail of a new contact person is made of this form */}
              <SelectInputFieldControlled
                id="emailFormat"
                label={t('Email format')}
                name="emailFormat"
                onChange={handleChange}
                options={[
                  <option key="" value="">
                    -
                  </option>,
                  // An example address of each form with what it is made of, e.g.
                  // "matti.meikalainen@yritys.fi (etunimi.sukunimi)"
                  ...EMAIL_FORMATS.map((format) => (
                    <option key={format} value={format}>
                      {`${makeEmail(
                        format,
                        t('Email example first name'),
                        t('Email example last name'),
                        customer.emailEnding.replace(/^@/, '') ||
                          t('Email ending example')
                      )} (${emailFormatNames[format] ?? format})`}
                    </option>
                  )),
                ]}
                value={customer.emailFormat}
              />
              <FormGroup>
                <Label className="label">
                  {t(`${translations.admin}:Organization Units`)}
                </Label>
                <MultiSelectDropdown
                  placeholder={t(`${translations.products}:Select`)}
                  required
                  name="organizationUnits"
                  value={organizationUnits}
                  onChange={handleOrganizationUnits}
                  options={
                    organizationUnitTypesList
                      ? showOrganizationUnits(organizationUnitTypesList)
                      : ''
                  }
                />
              </FormGroup>
            </Col>
            <Col>
              <TextInputField
                label={t('Business Id')}
                name="businessId"
                onChange={handleChange}
                value={customer.businessId}
              />
              <TextInputField
                label={t('Postal Number')}
                name="postalNumber"
                onChange={handleChange}
                value={customer.postalNumber}
              />
              {/* Any company can be a customer: the credit check with who marked it, the
                  credit rating and limit; changed only with the right to check credit */}
              <div className="contact-credit">
                <FormGroup>
                  <Label className="label" for="credit-checked-at">
                    {t('credit.Checked at')}
                    {customer.companyName ? (
                      <CreditCheckBadge
                        checkedAt={customer.creditCheckedAt || null}
                        companyName={customer.companyName}
                      />
                    ) : null}
                  </Label>
                  <div className="contact-credit-checked">
                    <DateInput
                      className="text-field"
                      disabled={!canCheckCredit}
                      id="credit-checked-at"
                      onChange={(value) =>
                        setCustomer((previous) => ({
                          ...previous,
                          creditCheckedAt: value,
                        }))
                      }
                      title={t(
                        `${translations.common}:creditCheck.Checked at help`
                      )}
                      value={customer.creditCheckedAt}
                    />
                    {/* The checker of the saved day */}
                    {savedCredit.checkedUserName &&
                    customer.creditCheckedAt === savedCredit.checkedAt ? (
                      <span className="contact-credit-checker">
                        {savedCredit.checkedUserName}
                      </span>
                    ) : null}
                  </div>
                </FormGroup>
                <Row>
                  <Col>
                    <SelectInputFieldControlled
                      disabled={!canCheckCredit}
                      id="creditRating"
                      label={t('credit.Rating')}
                      name="creditRating"
                      onChange={handleChange}
                      options={toOptions(creditRatings)}
                      value={customer.creditRating}
                    />
                  </Col>
                  <Col>
                    <TextInputField
                      disabled={!canCheckCredit}
                      label={`${t('credit.Limit')} €`}
                      name="creditLimit"
                      onChange={handleChange}
                      value={customer.creditLimit}
                    />
                  </Col>
                </Row>
                {canCheckCredit ? null : (
                  <p className="contact-credit-info">
                    {t('credit.No permission')}
                  </p>
                )}
              </div>

              <FormGroup>
                <Label className="contact-type-label label">
                  {t('Contact Types')}
                </Label>
                <Col sm={{ size: 10 }}>
                  {typeOptions.map((option) => {
                    // Every contact can be a customer, so the type is no longer chosen; it is
                    // kept (greyed) on the companies that have it
                    const retired = option.value === 'customer';
                    return (
                      <FormGroup check key={option.value}>
                        <Label
                          className={`contact-type-label ${
                            retired ? 'contact-type-retired' : ''
                          }`}
                          check
                          title={
                            retired ? t('Customer type retired') : undefined
                          }
                        >
                          <Input
                            className="type-checkmark"
                            disabled={retired}
                            name={option.value}
                            type="checkbox"
                            checked={typeIncluded(option.value)}
                            onChange={handleMultiSelectChange}
                          />
                          {option.label}
                        </Label>
                      </FormGroup>
                    );
                  })}
                </Col>
              </FormGroup>
            </Col>
          </Row>
          <Row>
            {contactId !== undefined ? (
              <Col>
                <Label className="label block">{t('Persons')}</Label>
                <Button className="contact-button" onClick={addPerson}>
                  {t('Add Person')}
                </Button>
                <CompanyPersons contactId={contactId} names={persons} />
              </Col>
            ) : null}
          </Row>
        </Form>
        {/* The departments or areas of a saved company, each saved on its own */}
        {contactId !== undefined ? (
          <ContactBranches
            addMessage={addMessage}
            canWrite={canWrite}
            contactId={contactId}
            country={customer.country}
          />
        ) : null}
        {contactId !== undefined ? (
          <LinkedOffers contactId={contactId} />
        ) : null}
        <PrimaryButton className="contact-button" onClick={goPreviousPage}>
          {t('common:Back')}
        </PrimaryButton>
        {/* With only the right to read, the contact is not saved or deleted */}
        {canWrite ? (
          <PrimaryButton
            className="add-button-margin contact-button float-right"
            onClick={addUpdateContact}
          >
            {t('common:Save')}
          </PrimaryButton>
        ) : null}
        {/* Only the admins: a company in use is locked, one not in use can be removed */}
        {contactId !== undefined && canDelete && !inUse ? (
          <PrimaryButton
            className="add-button-margin contact-button"
            onClick={() => toggleDelete()}
          >
            {t('Delete Contact')}
          </PrimaryButton>
        ) : null}
        {contactId !== undefined && canDelete ? (
          <PrimaryButton
            className="add-button-margin contact-button"
            onClick={toggleLocked}
          >
            {locked
              ? t(`${translations.contacts}:lock.Unlock`)
              : t(`${translations.contacts}:lock.Lock`)}
          </PrimaryButton>
        ) : null}
        {/* Why there is no delete: the company has events */}
        {contactId !== undefined && canDelete && inUse ? (
          <p className="contact-in-use-info">
            {t(`${translations.contacts}:lock.In use`)}
          </p>
        ) : null}
        <ModalConfirm
          showing={modalDelete}
          setShowing={toggleDelete}
          header={t('common:Confirm Delete')}
          text={t(`${translations.common}:Confirm Delete`)}
          yesButton={t('common:Confirm')}
          noButton={t('common:Cancel')}
          confirm={deleteContact}
        />
        <ModalAccept
          showing={modalDeleteConfirmation}
          setShowing={toggleDeleteConfirmation}
          header=""
          text={
            deleteSuccess
              ? t(`${translations.contacts}:Delete Success`)
              : t(`${translations.contacts}:Delete Failure`)
          }
        />
      </div>
    </PageContainer>
  );
};

const mapStateToProps = (state: any) => ({
  organizationUnitTypesList: state.users.organizationUnits,
});

const mapDispatchToProps = (dispatch: any) => ({
  addMessage: (message: string) => dispatch(addMessageAction(message)),
  fetchOrganizationUnitTypesList: () => dispatch(fetchOrganizationUnits()),
});
export default connect(mapStateToProps, mapDispatchToProps)(CreateContact);
